import Button from '@/common/components/Button'
import Modal from '@/common/components/Modal'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import { FormTransformers } from '@/common/utils/formTransformers'
import { useClientCompanyBranchAvailableToAllocateSummary } from '@/features/privateCompanies/hooks/useClientCompanyBranchAvailableToAllocateSummary'
import { useUpdateGovernmentAllocationEntryProductCategory } from '@/features/privateCompanies/hooks/useUpdateGovernmentAllocationEntryProductCategory'
import {
  entryProductCategoryCurrencySchema,
  entryProductCategoryQuantitySchema,
  GovernmentAllocationEntryProductCategoryCurrencyValidationSchema,
  GovernmentAllocationEntryProductCategoryQuantityValidationSchema,
} from '@/features/privateCompanies/pages/CompanyGovernmentAllocations/schemas/update.schema'
import { EntryProductCategory } from '@/features/privateCompanies/pages/CompanyGovernmentAllocations/Update/components/EntryProductCategory'
import { EntryProductCategoryQuantity } from '@/features/privateCompanies/pages/CompanyGovernmentAllocations/Update/components/EntryProductCategoryQuantity'
import { updateGovernmentAllocationEntryProductCategoryDefaultValues } from '@/features/privateCompanies/pages/CompanyGovernmentAllocations/Update/utils/updateGovernmentAllocationEntryProductCategoryDefaultValues'
import { ClientCompanyAllowedCategoriesAndProducts } from '@/features/privateCompanies/types/allowedCategoriesAndProducts.type'
import { ControlledByType } from '@/features/privateCompanies/types/companyGovernmentAllocation.type'
import { CompanyGovernmentAllocationEntryProductCategory } from '@/features/privateCompanies/types/companyGovernmentAllocationEntry.type'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { FileEditIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'

interface UpdateEntryProductCategoryModalProps {
  companyBranchId?: string
  governmentAllocationId: string
  entryProductCategory: CompanyGovernmentAllocationEntryProductCategory
  controlledBy?: ControlledByType
}

export const UpdateEntryProductCategoryModal = ({
  companyBranchId,
  governmentAllocationId,
  entryProductCategory,
  controlledBy = 'currency',
}: UpdateEntryProductCategoryModalProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const queryClient = useQueryClient()

  const [messages, setMessages] = useState<string[] | boolean>(false)

  const schema = controlledBy === 'quantity' ? entryProductCategoryQuantitySchema : entryProductCategoryCurrencySchema

  const {
    data: allowedCategoriesAndProducts
  } = useClientCompanyBranchAvailableToAllocateSummary<ClientCompanyAllowedCategoriesAndProducts>({
    companyBranchId,
    filters: {
      productCategoryId: entryProductCategory?.product_category?.id,
    },
  });

  const {
    mutate: updateGovernmentAllocationEntryProductCategories,
    isPending: updateGovernmentAllocationEntryProductCategoriesIsPending,
  } = useUpdateGovernmentAllocationEntryProductCategory({
    governmentAllocationId,
    entryProductCategory,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companyGovernmentAllocationEntry', governmentAllocationId] })
      queryClient.invalidateQueries({ queryKey: ['clientCompanyBranchAvailableToAllocateSummary'] })
      setIsOpen(false)
    },
    onError: (error) => {
      FormTransformers.errorsTransformer({ setError, setMessages })(error)
    },
  })

  const formMethods = useForm<GovernmentAllocationEntryProductCategoryCurrencyValidationSchema | GovernmentAllocationEntryProductCategoryQuantityValidationSchema>({
    resolver: zodResolver(schema),
    defaultValues: updateGovernmentAllocationEntryProductCategoryDefaultValues(entryProductCategory, controlledBy),
  })

  const { handleSubmit, setError, reset } = formMethods

  const onSubmit: SubmitHandler<any> = (form) =>
    updateGovernmentAllocationEntryProductCategories(form)

  useEffect(() => {
    if (isOpen) {
      reset(updateGovernmentAllocationEntryProductCategoryDefaultValues(entryProductCategory, controlledBy))
    }
  }, [entryProductCategory.entry_products.length, isOpen])

  return (
    <>
      <Modal
        isOpen={isOpen}
        setIsOpen={(value) => {
          setIsOpen(value)

          if (!value) {
            setTimeout(reset, 500)
          }
        }}
        title="Detalhando crédito por serviço"
        showClose
        Trigger={
          <Button variant="outline-text" className="h-10 w-auto p-0">
            <FileEditIcon strokeWidth={1} />
          </Button>
        }
        className="min-w-[640px] overflow-hidden"
        useCancellableFooter
        onConfirm={handleSubmit(onSubmit)}
        confirmButtonText="Salvar"
        confirmButtonClassName="bg-primary-blue-hover"
        disabled={updateGovernmentAllocationEntryProductCategoriesIsPending}
        confirmButtonIsLoading={updateGovernmentAllocationEntryProductCategoriesIsPending}
        body={
          <FormProvider {...formMethods}>
            <div className="max-h-[calc(100vh-10rem)] min-h-[360px] overflow-y-auto flex flex-col gap-6 px-1 text-white-800">
              {controlledBy === 'quantity' ? (
                <EntryProductCategoryQuantity
                  companyBranchId={companyBranchId}
                  entryProductCategory={entryProductCategory}
                  disabled={updateGovernmentAllocationEntryProductCategoriesIsPending}
                  allowedCategorySummary={allowedCategoriesAndProducts?.categories?.[entryProductCategory.product_category.id]}
                />
              ) : (
                <EntryProductCategory
                  companyBranchId={companyBranchId}
                  entryProductCategory={entryProductCategory}
                  disabled={updateGovernmentAllocationEntryProductCategoriesIsPending}
                  allowedCategorySummary={allowedCategoriesAndProducts?.categories?.[entryProductCategory.product_category.id]}
                />
              )}
            </div>
          </FormProvider>
        }
      />

      {messages && <ModalMessageSimple messages={messages} setMessages={setMessages} isError />}
    </>
  )
}
